import { AfterViewInit, ChangeDetectorRef, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import * as AOS from 'aos';
import { Apollo } from 'apollo-angular';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { AuthService } from '../../../services/auth.service';
import { NotificationService } from '../../../services/notification.service';
import { SettingsService } from '../../../services/settings.service';
import { StorageService } from '../../../services/storage.service';
import { LayoutService } from '../../../shared/components/app-layout/shared-layout/layout.service';
import { AppState } from '../../../store';
import { GET_PUBLICATION } from '../../../store/entities/epaper-management/epaper.graphql';
import { Publication } from '../../../store/entities/epaper-management/epaper.model';
import { READ_ATTACHMENT_BY_PATH } from '../../../store/entities/shared/attachment.graphql';
import { StoreItemActions } from '../../../store/entities/store-item/store-item.actions';
import { StoreItemEntity } from '../../../store/entities/store-item/store-item.model';

@Component({
  selector: 'app-corporate-magazines-trends',
  templateUrl: './corporate-magazines-trends.component.html',
  styleUrl: './corporate-magazines-trends.component.scss'
})
export class CorporateMagazinesTrendsComponent implements OnInit, OnDestroy, AfterViewInit {
  private readonly settings = inject(SettingsService);
  private readonly notify = inject(NotificationService);
  private readonly store = inject(Store<AppState>);
  private readonly layoutService = inject(LayoutService);
  private readonly apollo = inject(Apollo);
  private readonly router = inject(Router);
  private readonly cdr = inject(ChangeDetectorRef);
  private storageService = inject(StorageService);
  private notificationService: NotificationService = inject(NotificationService);
  private authService: AuthService = inject(AuthService);

  title = 'Publication Management';

  query = GET_PUBLICATION;
  upsertAction = StoreItemActions.upsertStoreItem;

  additionalVariables: any = {};

  deleteAction = StoreItemActions.deleteStoreItem;
  storeEntity: StoreItemEntity = StoreItemEntity.gazeti;

  searchControl = new FormControl('');

  // Magazine carousel properties
  magazines: Publication[] = [];
  selectedPublication?: Publication;
  loading = false;
  currentIndex = 0;
  visibleCount = 3;
  slideWidth = 100 / 3;
  isHovering = false;
  private autoPlayInterval?: any;

  // PDF viewer properties
  showFullPdfViewer = false;
  currentMagazinePdf: string | null = null;
  currentMagazineTitle: string = '';

  // magazine-trend-section.component.ts

  ngOnInit() {
    this.additionalVariables = {
      filtering: {
        pageNumber: 0,
        publicationType: 'MAGAZINE'
      }
    };

    this.searchControl.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(keyword => {
      this.additionalVariables.filtering.keyword = keyword;
      this.authenticateAndLoadPublications();
    });

    // First authenticate, then load publications
    this.authenticateAndLoadPublications();
    this.updateVisibleCount();
    this.startAutoPlay();
  }

  private async authenticateAndLoadPublications() {
    try {
      // Get publication client token first
      const tokenResult = await this.authService.loginClient({});

      if (tokenResult?.accessToken) {
        console.log('✅ Publication client authenticated');
        // Now load publications with the token in place
        this.loadPublications();
      } else {
        this.notify.errorMessage('Failed to authenticate publication client');
        this.loading = false;
      }
    } catch (error) {
      console.error('Authentication error:', error);
      this.notify.errorMessage('Failed to authenticate');
      this.loading = false;
    }
  }

  ngAfterViewInit() {
    AOS.init({
      duration: 800,
      easing: 'ease-in-out',
      once: true
    });

    window.addEventListener('resize', () => this.updateVisibleCount());
  }

  ngOnDestroy() {
    this.stopAutoPlay();
    window.removeEventListener('resize', () => this.updateVisibleCount());
  }

  // Load attachment method
  loadAttachment(filePath: string) {
    return this.apollo.query({
      query: READ_ATTACHMENT_BY_PATH,
      variables: {
        input: {
          filePath: filePath
        }
      },
      fetchPolicy: 'no-cache'
    });
  }

  loadPublications() {
    this.loading = true;

    // Get publication token
    const publicationToken = this.storageService.get('publicationClient');
    console.log(publicationToken)
    this.apollo.watchQuery<{ getPublications: { data: Publication[] } }>({
      query: this.query,
      variables: this.additionalVariables,
      fetchPolicy: 'network-only',
      context: {
        headers: {
          Authorization: `Bearer ${publicationToken}`
        }
      }
    }).valueChanges.subscribe({
      next: (res) => {
        const rawMagazines = res.data.getPublications.data;

        this.magazines = rawMagazines.map(mag => ({
          ...mag,
          thumbnailUrl: null,
          pdfUrl: null,
          isLoadingThumb: true,
          isLoadingPdf: false
        }));

        this.loading = false;
        this.cdr.detectChanges();
        this.loadMagazineAttachments();
      },
      error: (err) => {
        console.error('Error loading publications:', err);
        this.notify.errorMessage('Failed to load publications');
        this.loading = false;
      }
    });
  }
  private loadMagazineAttachments() {
    // Load thumbnails sequentially to prevent flickering
    this.magazines.forEach((magazine, index) => {
      if (magazine.thumbnail) {
        this.loadAttachment(magazine.thumbnail).subscribe({
          next: (thumbRes: any) => {
            const base64Data = thumbRes?.data?.getAttachment?.data?.filePath;
            if (base64Data) {
              this.magazines[index] = {
                ...this.magazines[index],
                thumbnailUrl: `data:image/png;base64,${base64Data}`,
                isLoadingThumb: false
              };
              this.cdr.detectChanges();
            }
          },
          error: (err) => {
            console.error('Error loading thumbnail:', err);
            this.magazines[index] = {
              ...this.magazines[index],
              thumbnailUrl: null,
              isLoadingThumb: false
            };
            this.cdr.detectChanges();
          }
        });
      }
    });
  }

  // Carousel navigation methods
  nextSlide() {
    if (this.currentIndex < this.magazines.length - this.visibleCount) {
      this.currentIndex++;
    }
  }

  previousSlide() {
    if (this.currentIndex > 0) {
      this.currentIndex--;
    }
  }

  onCarouselHover(isHovering: boolean) {
    this.isHovering = isHovering;
    if (isHovering) {
      this.stopAutoPlay();
    } else {
      this.startAutoPlay();
    }
  }

  startAutoPlay() {
    this.stopAutoPlay();
    this.autoPlayInterval = setInterval(() => {
      if (!this.isHovering) {
        if (this.currentIndex >= this.magazines.length - this.visibleCount) {
          this.currentIndex = 0;
        } else {
          this.nextSlide();
        }
      }
    }, 5000);
  }

  stopAutoPlay() {
    if (this.autoPlayInterval) {
      clearInterval(this.autoPlayInterval);
      this.autoPlayInterval = undefined;
    }
  }

  updateVisibleCount() {
    const width = window.innerWidth;
    if (width < 640) {
      this.visibleCount = 1;
      this.slideWidth = 100;
    } else if (width < 1024) {
      this.visibleCount = 2;
      this.slideWidth = 50;
    } else {
      this.visibleCount = 3;
      this.slideWidth = 100 / 3;
    }
  }

  // magazine-trend-section.component.ts

  // Update the openMagazineReader method to open in new tab
  openMagazineReader(publication: Publication, event?: Event) {
    if (event) {
      event.stopPropagation();
    }

    if (!publication?.path) {
      this.notify.errorMessage('Magazine not available');
      return;
    }

    // Show loading
    this.loading = true;

    // Load PDF
    this.loadAttachment(publication.path).subscribe({
      next: (res: any) => {
        const base64Data = res?.data?.getAttachment?.data?.filePath;
        if (base64Data) {
          // Convert base64 to blob and open in new tab
          this.openPdfInNewTab(base64Data, publication.title);
          this.notify.successMessage(`Opening ${publication.title}`);
        } else {
          this.notify.errorMessage('Failed to load magazine');
        }
        this.loading = false;
      },
      error: (err) => {
        console.error('Error loading magazine:', err);
        this.notify.errorMessage('Failed to load magazine');
        this.loading = false;
      }
    });
  }

  // Add new method to open PDF in new tab
  private openPdfInNewTab(base64Data: string, title: string) {
    try {
      // Convert base64 to blob
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });

      // Create object URL
      const blobUrl = URL.createObjectURL(blob);

      // Open in new tab
      const newWindow = window.open(blobUrl, '_blank');

      if (!newWindow) {
        this.notify.errorMessage('Please allow popups to view the PDF');
      } else {
        // Set title for the new tab
        newWindow.document.title = title;

        // Clean up the blob URL after a delay
        setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
      }
    } catch (error) {
      console.error('Error opening PDF:', error);
      this.notify.errorMessage('Failed to open PDF');
    }
  }

  // Add method to check if navigation arrows should be shown
  get showNavigationArrows(): boolean {
    return this.magazines.length > this.visibleCount;
  }

  // Add method to check if we can navigate
  get canNavigateNext(): boolean {
    return this.currentIndex < this.magazines.length - this.visibleCount;
  }

  get canNavigatePrev(): boolean {
    return this.currentIndex > 0;
  }

  // Close magazine reader
  closeMagazineReader() {
    this.showFullPdfViewer = false;
    this.currentMagazinePdf = null;
    this.currentMagazineTitle = '';
  }

  // Download magazine
  downloadPdf(publication: Publication, event: Event) {
    event.stopPropagation();

    if (!publication?.path) {
      this.notify.errorMessage('Magazine not available for download');
      return;
    }

    this.loading = true;
    this.loadAttachment(publication.path).subscribe({
      next: (res: any) => {
        const base64Data = res?.data?.getAttachment?.data?.filePath;
        if (base64Data) {
          const dataUrl = `data:application/pdf;base64,${base64Data}`;
          this.downloadFromDataUrl(dataUrl, publication.title);
        } else {
          this.notify.errorMessage('Failed to download magazine');
        }
        this.loading = false;
      },
      error: (err) => {
        console.error('Error downloading magazine:', err);
        this.notify.errorMessage('Failed to download magazine');
        this.loading = false;
      }
    });
  }

  private downloadFromDataUrl(dataUrl: string, title: string) {
    const link = document.createElement('a');
    link.href = dataUrl;
    link.download = `${this.sanitizeFilename(title)}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    this.notify.successMessage(`Downloading ${title}`);
  }

  sanitizeFilename(filename: string): string {
    return filename
      .replace(/[^a-z0-9]/gi, '_')
      .replace(/_+/g, '_')
      .toLowerCase();
  }

  // TrackBy function to prevent re-rendering
  trackByMagazineId(index: number, magazine: Publication): number {
    return magazine.id;
  }

  // Utility methods
  formatDate(date: string | Date): string {
    if (!date) return '';
    const d = new Date(date);
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  onImageError(event: any) {
    event.target.src = 'assets/images/default-magazine.jpg';
  }

  closePanel() {
    this.layoutService.closePanel();
  }
}
